import React from "react";
import { Link, NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Inbox,
  LogOut,
  Sparkles,
  ExternalLink,
  ImagePlus,
  Users,
  MessageSquare,
  Mail,
  Home,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { FREEAYTO_LOGO } from "../../data/mock";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/leads", label: "Anfragen", icon: Inbox },
  { to: "/admin/homepage", label: "Homepage", icon: Home },
  { to: "/admin/erfolge", label: "Erfolge", icon: ImagePlus },
  { to: "/admin/team", label: "Team", icon: Users },
  { to: "/admin/testimonials", label: "Stimmen", icon: MessageSquare },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
];

const AdminLayout = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center">
        <div className="text-sm text-neutral-400">Laden…</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0 bg-black border-r border-white/5">
        <div className="px-6 py-6 border-b border-white/5">
          <Link to="/admin" className="flex items-center gap-2">
            <img src={FREEAYTO_LOGO} alt="FREEAYTO" className="h-10 w-auto" />
          </Link>
          <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-pink-400">
            <Sparkles size={12} />
            Backoffice
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((item) => {
            const Ico = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-white/10 text-white"
                      : "text-neutral-400 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <Ico size={16} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/5 space-y-2">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-xs text-neutral-400 hover:text-white hover:bg-white/5"
          >
            <ExternalLink size={14} /> Website ansehen
          </a>
          <div className="px-4 py-3 rounded-xl bg-white/5 border border-white/10">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
              Eingeloggt als
            </div>
            <div className="text-sm font-bold text-white truncate">
              {user.username}
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-neutral-300 hover:bg-red-500/10 hover:text-red-400 transition-colors"
          >
            <LogOut size={16} /> Abmelden
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-black border-b border-white/10 px-4 h-14 flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-2">
          <img src={FREEAYTO_LOGO} alt="FREEAYTO" className="h-8 w-auto" />
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-pink-400">
            Backoffice
          </span>
        </Link>
        <button
          onClick={handleLogout}
          className="p-2 text-neutral-300 hover:text-red-400"
          aria-label="Abmelden"
        >
          <LogOut size={18} />
        </button>
      </div>

      {/* Mobile bottom nav */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-black border-t border-white/10 grid grid-cols-7">
        {NAV.map((item) => {
          const Ico = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-3 text-[11px] font-semibold ${
                  isActive ? "text-pink-400" : "text-neutral-400"
                }`
              }
            >
              <Ico size={18} />
              {item.label}
            </NavLink>
          );
        })}
      </div>

      <main className="flex-1 lg:ml-0 pt-14 pb-20 lg:pt-0 lg:pb-0 overflow-y-auto">
        <div className="max-w-[1300px] mx-auto p-6 md:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
